import PERMISSIONS from '@/constants/permissions';
import { toFlagEmoji } from '@/utils/parsePhone';
// Journey Emails ('journeys') is unwired pending backend blockers — its
// components stay under tabs/ for later.
// Theme and Translations used to be tabs here; they are Touchpoints pages of
// their own now (Design / Translations), and `legacyTabRedirect` forwards the
// links that still address them as tabs. 'layout' went the other way: the
// layouts & building blocks were the Design page's Email design section and are
// a tab here, because they are partner-wide and belong with the templates they
// wrap rather than with a journey's theme.
// 'automations' was a tab too: what an automation does with a mail is read in
// that mail's editor now, so an old `?tab=automations` link falls back to
// Templates like any other unknown value.
export const EMAIL_CENTER_TABS = {
  TEMPLATES: 'templates',
  LAYOUT: 'layout',
  MAILERS: 'mailers',
} as const;
export type EmailCenterTab =
  (typeof EMAIL_CENTER_TABS)[keyof typeof EMAIL_CENTER_TABS];

/**
 * Tab values this page used to serve, kept **only** to migrate links.
 *
 * `settings` was one tab holding two segmented sections; `theme` and
 * `translations` were tabs that are now their own pages under Touchpoints. A
 * bookmarked link to any of them still has to land where the content went
 * rather than silently on Templates, which is what `parseEmailCenterSearch` and
 * `legacyTabRedirect` use these for. Nothing renders from them.
 */
export const LEGACY_TABS = {
  SETTINGS: 'settings',
  THEME: 'theme',
  TRANSLATIONS: 'translations',
} as const;

/**
 * The `email_driver` values the page reacts to.
 *
 * A journey on NL2Go sends through Newsletter2Go: our mailers are Mailcoach
 * routing and mean nothing for it, so the Mailers tab has nothing it could set.
 * NL2Go is being retired journey by journey, which is why this is read per
 * selected lead source rather than per partner.
 */
export const EMAIL_DRIVERS = {
  MAILCOACH: 'mailcoach',
  NL2GO: 'nl2go',
} as const;

/** Sections of the retired Settings tab, addressed as `?section=…`. */
export const LEGACY_SETTINGS_SECTIONS = {
  MAILERS: 'mailers',
  TRANSLATIONS: 'translations',
} as const;

/**
 * The Design page's retired Email design section, addressed as `?section=design`.
 *
 * Its content is the Layout & Components tab of this page now. Kept only to
 * migrate the links — a Design-page bookmark, and the `?tab=theme&section=design`
 * ones from when the whole editor was a tab here.
 */
export const LEGACY_DESIGN_SECTION = 'design';

// API-side limits (backend contract).
export const REPLY_TO_MAX = 5;
export const BCC_MAX = 10;

// The templates list is paginated with no search param; the Templates tab and
// the pickers fetch one max-size page per type and warn when the total
// exceeds it (trigger coverage is computed client-side).
export const TEMPLATE_POOL_PAGE_SIZE = 250;

// The 13 backoffice locales as 2-char codes (link language, translation values).
export const LANGUAGE_CODES = [
  'de',
  'en',
  'cs',
  'es',
  'fr',
  'hr',
  'hu',
  'nl',
  'ro',
  'sk',
  'sl',
  'sr',
  'sv',
];

/**
 * Language code → the country whose flag stands for it.
 *
 * Needed because a flag emoji is built from a *country* code, and for most of
 * these locales the two differ (`cs`→CZ, `sv`→SE, `sl`→SI, `sr`→RS, `da`-style
 * mismatches). `en` uses GB rather than US: this is the EU backoffice, and the
 * partners reading it are European.
 */
export const LANGUAGE_COUNTRY: Record<string, string> = {
  de: 'DE',
  en: 'GB',
  cs: 'CZ',
  es: 'ES',
  fr: 'FR',
  hr: 'HR',
  hu: 'HU',
  nl: 'NL',
  ro: 'RO',
  sk: 'SK',
  sl: 'SI',
  sr: 'RS',
  sv: 'SE',
};

/**
 * The flag for a language code, or '' when the code is not one we map.
 *
 * Returning '' rather than a placeholder keeps an unmapped code readable — the
 * label still carries the code itself, which is the part that identifies it.
 */
export function languageFlag(code: string): string {
  const country = LANGUAGE_COUNTRY[code?.toLowerCase()];
  return country ? toFlagEmoji(country) : '';
}

/** `'🇩🇪 DE'` — the flag scans first, the code disambiguates it. */
export function languageLabel(code: string): string {
  const flag = languageFlag(code);
  return flag ? `${flag} ${code.toUpperCase()}` : code.toUpperCase();
}

/**
 * The same codes as `<Select>` options, flagged and uppercased for display.
 *
 * Five surfaces built this array inline (both template drawers, the preview drawer,
 * the translation field and the settings drawer); a constant derived from a constant
 * cannot drift.
 *
 * The label is a **string**, not a node, so it stays usable in the places that
 * treat an option label as text (search/filter matching) as well as in a `<Select>`.
 */
export const LANGUAGE_OPTIONS = LANGUAGE_CODES.map((code) => ({
  value: code,
  label: languageLabel(code),
}));

/**
 * Whether a new template may be created for a specific (non-default) lead source.
 *
 * Off for now: every new transactional template lands on the default journey,
 * which every lead source without its own email inherits. With one template per
 * event the create step has no scope question left, so the drawer hides the lead
 * source picker and an event with a template is closed off in the create menu.
 * Flip back to `true` to restore the per-lead-source create flow.
 */
export const LEAD_SOURCE_TEMPLATES_ENABLED: boolean = false;

/**
 * Who may open each of this page's drawers.
 *
 * They are mounted globally in `DvaWatcher`, so the mount is the only place that
 * can keep an unauthorised user out of one no matter which opener — or deep link —
 * reaches for it. All four sets were one `[BUSINESS_PARTNER]` gate, which made the
 * whole Email Center superuser-only: a user holding `email_template_view` saw the
 * list, clicked View, and got nothing at all. Found in the browser with seven roles
 * created for it.
 *
 * `BUSINESS_PARTNER` stays in every set — `useHasAccess` appends it anyway, and
 * `CheckAccess` does not, so leaving it out here would lock out the superusers who
 * are the only ones using these drawers today.
 */
export const DRAWER_ACCESS = {
  /**
   * Reading a template is enough to open its editor: the drawer decides for
   * itself what is editable (`readOnly`, `canPublish`), and view-only lands in a
   * read-only canvas with only Send test offered.
   */
  template: [
    PERMISSIONS.EmailTemplate.VIEW,
    PERMISSIONS.EmailTemplate.DRAFT,
    PERMISSIONS.EmailTemplate.PUBLISH,
    PERMISSIONS.EmailTemplate.CREATE,
    PERMISSIONS.BUSINESS_PARTNER,
  ],
  /**
   * A journey's sender, UTM parameters and template variables, and which template
   * an event sends — none of it private or reversible, so it is publish rather
   * than draft.
   *
   * **These two have no permission of their own in the backend catalogue**
   * (`docs/access-management.md` lists none, and neither surface had any check at
   * all before this). Mapped to the nearest existing one rather than inventing a
   * constant the backend does not enforce; the Touchpoints policies are the place
   * to settle it.
   */
  journeyEmails: [
    PERMISSIONS.EmailTemplate.PUBLISH,
    PERMISSIONS.BUSINESS_PARTNER,
  ],
  mailer: [PERMISSIONS.Mailers.MANAGE, PERMISSIONS.BUSINESS_PARTNER],
  translation: [
    PERMISSIONS.PartnerTranslations.EDIT,
    PERMISSIONS.BUSINESS_PARTNER,
  ],
} as const;
